'use strict';

/** @typedef {import('./pipelineProgressModel').PipelineProgressStep} PipelineProgressStepModel */

import React from 'react';
import { Box, Step, StepButton, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import PendingIcon from '@mui/icons-material/Pending';

/**
 * State → icon (state is always also conveyed with text; never colour alone).
 * Colour comes from an explicit wrapper (`color` + `svg { color: inherit }` reset in the
 * summary's Stepper sx) so the app-wide wizard-stepper svg theme override cannot blank it.
 */
function stepIconFor(state, isCurrent) {
	const icon = (() => {
		switch (state) {
			case 'complete':
				return { node: <CheckCircleIcon fontSize="small" color="inherit" titleAccess="Completed" />, color: 'success.main' };
			case 'failed':
				return { node: <CancelIcon fontSize="small" color="inherit" titleAccess="Failed" />, color: 'error.main' };
			case 'delayed':
				return { node: <WarningAmberIcon fontSize="small" color="inherit" titleAccess="Delayed" />, color: 'warning.main' };
			case 'active':
				return { node: <PendingIcon fontSize="small" color="inherit" titleAccess="In progress" />, color: 'info.main' };
			default:
				return {
					node: (
						<RadioButtonUncheckedIcon
							fontSize="small"
							color="inherit"
							titleAccess={isCurrent ? 'Current step' : 'Upcoming'}
						/>
					),
					color: isCurrent ? 'info.main' : 'text.disabled',
				};
		}
	})();
	return (
		<Box component="span" sx={{ display: 'inline-flex', color: icon.color }}>
			{icon.node}
		</Box>
	);
}

/** Screen-reader state suffix so the step never relies on the icon/colour alone. */
function stateText(state, isCurrent) {
	switch (state) {
		case 'complete':
			return 'completed';
		case 'failed':
			return 'failed';
		case 'delayed':
			return 'delayed';
		case 'active':
			return 'in progress';
		default:
			return isCurrent ? 'current' : 'upcoming';
	}
}

const visuallyHiddenSx = {
	position: 'absolute',
	width: 1,
	height: 1,
	overflow: 'hidden',
	clip: 'rect(0 0 0 0)',
	whiteSpace: 'nowrap',
};

/**
 * One Level-1 tracker step. Rendered inside a MUI `Stepper`; clicking selects the stage for
 * the Level 2 details section. Remaining props (orientation context etc.) forward to `Step`.
 *
 * `inactive` renders the step greyed-out and non-interactive — used when progress tracking
 * is unavailable for the project, so the bar keeps its familiar shape without claiming any
 * progress state.
 *
 * @param {{
 *   step: PipelineProgressStepModel;
 *   isCurrent: boolean;
 *   onSelect: (stepId: string) => void;
 *   inactive?: boolean;
 *   [key: string]: unknown;
 * }} props
 */
export function PipelineProgressStep({ step, isCurrent, onSelect, inactive = false, ...stepProps }) {
	const failed = !inactive && step.state === 'failed';

	if (inactive) {
		return (
			<Step completed={false} disabled {...stepProps}>
				<StepButton
					disabled
					aria-disabled="true"
					data-testid={`pipeline-progress-step-${step.id}`}
					icon={
						<Box component="span" sx={{ display: 'inline-flex', color: 'text.disabled' }}>
							<RadioButtonUncheckedIcon fontSize="small" color="inherit" titleAccess="Not tracked" />
						</Box>
					}
				>
					<Box component="span" sx={{ fontWeight: 500, color: 'text.disabled' }}>
						{step.label}
						<Typography component="span" sx={visuallyHiddenSx}>
							{' (not tracked)'}
						</Typography>
					</Box>
				</StepButton>
			</Step>
		);
	}

	return (
		<Step completed={step.state === 'complete'} {...stepProps}>
			<StepButton
				onClick={() => onSelect(step.id)}
				aria-current={isCurrent ? 'step' : undefined}
				data-testid={`pipeline-progress-step-${step.id}`}
				sx={{
					borderRadius: 1,
					// MUI StepButton defaults to padding 24/16 with matching NEGATIVE margins,
					// extending the click target ~24px above the step — under our header row,
					// where it swallowed clicks meant for the "Hide details" button. Constrain
					// the hit area to the step's own box.
					m: 0,
					px: 1,
					py: 0.5,
					'&:focus': { outline: 'none' },
					'&:focus-visible': { outline: '2px solid', outlineColor: 'info.main', outlineOffset: '2px' },
				}}
				icon={stepIconFor(step.state, isCurrent)}
				optional={
					step.count ? (
						<Typography variant="caption" color="text.secondary" component="span">
							{step.count.completed} of {step.count.total} sample sets
						</Typography>
					) : undefined
				}
			>
				<Box component="span" sx={{ fontWeight: isCurrent ? 600 : 500, color: failed ? 'error.main' : 'text.primary' }}>
					{step.label}
					<Typography component="span" sx={visuallyHiddenSx}>
						{` (${stateText(step.state, isCurrent)})`}
					</Typography>
				</Box>
			</StepButton>
		</Step>
	);
}
